/* eslint-disable no-unused-vars */
import {
  AddIcon,
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import ReactSelect from "react-select";
import LoadingSpinner from "../../LoadingSpinner";
import { useDropdownOptions } from "../../hooks/useDropdownOptions";
import { selectStyles } from "../../utils/selectStyel";

export default function EditModelConfigModal({
  data,
  setData,
  loading,
  error,
  onRefresh,
}) {
  const toast = useToast();
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState("");
  const { options: caseTypeOptions } = useDropdownOptions("CASE_TYPE");

  const makeKey = (category, id) => `${category}__${id}`;

  // ----------------- Model actions -----------------
  const startAdd = (category) => {
    const newId = `NEW_${Date.now()}`;
    const newModel = { CONFIG_ID: newId, Name: "", TYPES: [], _ts: Date.now() };
    setData((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), newModel],
    }));
    setEditingKey(makeKey(category, newId));
    setDraft({ ...newModel, category });
  };

  const startEdit = (model, category) => {
    setEditingKey(makeKey(category, model.CONFIG_ID));
    setDraft({ ...model, category });
  };

  const cancelEdit = () => {
    if (!draft) {
      setEditingKey(null);
      return;
    }
    if (draft.CONFIG_ID?.startsWith("NEW_")) {
      setData((prev) => {
        const arr = prev[draft.category].filter(
          (m) => m.CONFIG_ID !== draft.CONFIG_ID,
        );
        return { ...prev, [draft.category]: arr };
      });
    }
    setEditingKey(null);
    setDraft(null);
  };

  const handleDelete = async (configId, category) => {
    const ok = window.confirm(`Delete ${configId} from ${category}?`);
    if (!ok) return;

    // optimistic local update
    setData((prev) => {
      const arr = prev[category].filter((m) => m.CONFIG_ID !== configId);
      return { ...prev, [category]: arr };
    });

    try {
      const res = await fetch(`/api/model?id=${encodeURIComponent(configId)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`Failed to delete: ${res.status}`);
      toast({
        status: "info",
        description: `${configId} deleted.`,
        duration: 2500,
      });
      onRefresh(); // sync with server
    } catch (err) {
      console.error(err);
      toast({ status: "error", description: "Failed to delete model." });
      onRefresh(); // revert optimistic update by re-fetching
    }

    if (editingKey === makeKey(category, configId)) {
      setEditingKey(null);
      setDraft(null);
    }
  };

  const handleSaveModel = async () => {
    if (!draft || !draft.category) return;
    const category = draft.category;

    if (!draft.CONFIG_ID || !draft.CONFIG_ID.trim()) {
      toast({ status: "error", description: "CONFIG_ID is required." });
      return;
    }

    const exists = (data[category] || []).some(
      (m) =>
        m.CONFIG_ID === draft.CONFIG_ID &&
        makeKey(category, m.CONFIG_ID) !== editingKey,
    );
    if (exists) {
      toast({
        status: "error",
        description: `CONFIG_ID "${draft.CONFIG_ID}" already exists.`,
      });
      return;
    }

    const updatedModel = { ...draft, _ts: Date.now() };

    // optimistic local update
    setData((prev) => {
      const arr = prev[category] || [];
      const next = arr.map((m) =>
        makeKey(category, m.CONFIG_ID) === editingKey ? updatedModel : m,
      );
      return { ...prev, [category]: next };
    });

    try {
      const res = await fetch(
        `/api/model?id=${encodeURIComponent(updatedModel.CONFIG_ID)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedModel),
        },
      );
      if (!res.ok) throw new Error(`Failed to save: ${res.status}`);
      toast({
        status: "success",
        description: `${draft.CONFIG_ID} saved.`,
        duration: 2000,
      });
      onRefresh(); // sync SearchModelList instantly
    } catch (err) {
      console.error(err);
      toast({ status: "error", description: "Failed to save model." });
      onRefresh(); // revert by re-fetching
    }

    setEditingKey(null);
    setDraft(null);
  };

  const updateDraftField = (field, value) =>
    setDraft((d) => ({ ...d, [field]: value }));

  // ----------------- Category actions -----------------
  const categories = useMemo(() => Object.keys(data), [data]);

  const addCategory = () => {
    if (!newCategoryName.trim()) return;
    if (data[newCategoryName]) {
      toast({ status: "error", description: "Category already exists" });
      return;
    }
    setData((prev) => ({ ...prev, [newCategoryName]: [] }));
    setNewCategoryName("");
    toast({ status: "success", description: "Category added" });
  };

  const deleteCategory = (category) => {
    const ok = window.confirm(`Delete entire category "${category}"?`);
    if (!ok) return;
    setData((prev) => {
      const copy = { ...prev };
      delete copy[category];
      return copy;
    });
    toast({ status: "info", description: `Category "${category}" deleted` });
  };

  const renameCategory = (oldName, newName) => {
    if (!newName.trim()) return;
    if (data[newName]) {
      toast({ status: "error", description: "Category name already exists" });
      return;
    }
    setData((prev) => {
      const copy = { ...prev };
      copy[newName] = copy[oldName];
      delete copy[oldName];
      return copy;
    });
    toast({ status: "success", description: "Category renamed" });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box height="75vh">
      <Flex mb={3} align="center" gap={2}>
        <Input
          size="sm"
          placeholder="New Category"
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
        />
        <Button size="sm" colorScheme="green" onClick={addCategory}>
          Add Category
        </Button>
      </Flex>

      <Tabs variant="line">
        <TabList mb="1em">
          {categories.map((c) => (
            <Tab key={c}>
              <Flex align="center" gap={1}>
                {editingCategory === c ? (
                  <Input
                    size="xs"
                    value={draft?.categoryName ?? c}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, categoryName: e.target.value }))
                    }
                    onBlur={() => {
                      renameCategory(c, draft.categoryName);
                      setEditingCategory(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        renameCategory(c, draft.categoryName);
                        setEditingCategory(null);
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <Text
                    onDoubleClick={() => {
                      setEditingCategory(c);
                      setDraft({ categoryName: c });
                    }}
                  >
                    {c}
                  </Text>
                )}
                <IconButton
                  bg="none"
                  size="xs"
                  aria-label="Delete category"
                  icon={<DeleteIcon color="red" />}
                  border="none"
                  onClick={() => deleteCategory(c)}
                />
              </Flex>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {categories.map((category) => (
            <TabPanel key={category} px={0}>
              <Flex justify="space-between" mb={3}>
                <Text fontSize="lg" fontWeight="bold">
                  {category} — {data[category]?.length ?? 0} models
                </Text>
                <Button
                  leftIcon={<AddIcon />}
                  size="sm"
                  colorScheme="blue"
                  onClick={() => startAdd(category)}
                >
                  Add Model
                </Button>
              </Flex>

              <TableContainer maxH="550px" overflowY="scroll">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>CONFIG_ID</Th>
                      <Th>Name</Th>
                      <Th>Types</Th>
                      <Th>_ts</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(data[category] || []).map((model) => {
                      const key = makeKey(category, model.CONFIG_ID);
                      const isEditing = editingKey === key;
                      return (
                        <Tr key={model.CONFIG_ID}>
                          <Td minW="180px" verticalAlign="center">
                            {isEditing ? (
                              <Input
                                size="sm"
                                value={draft.CONFIG_ID}
                                onChange={(e) =>
                                  updateDraftField("CONFIG_ID", e.target.value)
                                }
                                isDisabled={
                                  !draft.CONFIG_ID?.startsWith("NEW_") &&
                                  model.CONFIG_ID &&
                                  !model.CONFIG_ID.startsWith("NEW_")
                                }
                              />
                            ) : (
                              <Text fontFamily="monospace">
                                {model.CONFIG_ID}
                              </Text>
                            )}
                          </Td>

                          <Td>
                            {isEditing ? (
                              <Input
                                size="sm"
                                value={draft.Name ?? ""}
                                placeholder="Name"
                                onChange={(e) =>
                                  updateDraftField("Name", e.target.value)
                                }
                              />
                            ) : (
                              model.Name || <Text color="gray.500">—</Text>
                            )}
                          </Td>

                          <Td minW="260px">
                            {isEditing ? (
                              <ReactSelect
                                isMulti
                                options={caseTypeOptions}
                                value={(draft.TYPES || []).map((t) => {
                                  const found = caseTypeOptions.find(
                                    (c) => c.value === t,
                                  );
                                  return { label: found?.label || t, value: t };
                                })}
                                onChange={(selected) =>
                                  updateDraftField(
                                    "TYPES",
                                    (selected || []).map((s) => s.value),
                                  )
                                }
                                placeholder="Select or type to add"
                                isClearable
                                isCreatable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectStyles}
                              />
                            ) : model.TYPES?.length > 0 ? (
                              <HStack spacing={2} wrap="wrap">
                                {model.TYPES.map((t) => (
                                  <Tag key={t} size="sm" variant="subtle">
                                    <TagLabel>{t}</TagLabel>
                                  </Tag>
                                ))}
                              </HStack>
                            ) : (
                              <Text color="gray.500">—</Text>
                            )}
                          </Td>

                          <Td>
                            <Text fontSize="sm">
                              {new Date((model._ts || 0) * 1).toLocaleString()}
                            </Text>
                          </Td>

                          <Td>
                            {isEditing ? (
                              <HStack spacing={2}>
                                <Tooltip label="Save">
                                  <CheckIcon
                                    onClick={handleSaveModel}
                                    color="green"
                                    cursor="pointer"
                                  />
                                </Tooltip>
                                <Tooltip label="Cancel">
                                  <CloseIcon
                                    onClick={cancelEdit}
                                    color="black"
                                    cursor="pointer"
                                  />
                                </Tooltip>
                              </HStack>
                            ) : (
                              <HStack spacing={2}>
                                <Tooltip label="Edit">
                                  <EditIcon
                                    onClick={() => startEdit(model, category)}
                                    color="black"
                                    cursor="pointer"
                                  />
                                </Tooltip>
                                <Tooltip label="Delete">
                                  <DeleteIcon
                                    color="red"
                                    cursor="pointer"
                                    onClick={() =>
                                      handleDelete(model.CONFIG_ID, category)
                                    }
                                  />
                                </Tooltip>
                              </HStack>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
}
